import { Theme } from '@mui/material/styles';
import { Box, Slider, SliderThumb, styled, SxProps } from '@mui/material';
import React, { forwardRef } from 'react';

const StyledSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.secondary.main,
  height: 3,
  padding: '13px 0',
  '& .MuiSlider-mark': {
    backgroundColor: theme.palette.text.disabled,
    width: 3,
    height: 3,
    borderRadius: '50%',
  },
  '& .MuiSlider-markActive': {
    opacity: 1,
    backgroundColor: theme.palette.text.disabled,
  },

  '& .MuiSlider-thumb': {
    height: 20,
    width: 20,
    backgroundColor: theme.palette.background.paper,
    border: '1px solid currentColor',
    '&:hover': {
      boxShadow: `0 0 0 8px ${theme.palette.secondary.focusRing}`,
    },
    '& .airbnb-bar': {
      height: 9,
      width: 1,
      backgroundColor: 'currentColor',
      marginLeft: 1,
      marginRight: 1,
    },
  },
  '& .MuiSlider-track': {
    height: 3,
    backgroundColor: theme.palette.secondary.main,
    border: 'none',
  },
  '& .MuiSlider-rail': {
    color: theme.palette.background.surface,
    opacity: 1,
    height: 3,
  },
}));

function ThumbComponent({ children, ...other }: React.HTMLAttributes<unknown>) {
  return (
    <SliderThumb {...other}>
      {children}
      <span className='airbnb-bar' />
      <span className='airbnb-bar' />
      <span className='airbnb-bar' />
    </SliderThumb>
  );
}

export interface MarkedSliderProperties {
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number | number[];
  value?: number | number[];
  onChange?: (event: Event, value: number | number[]) => void;
  marks?: Array<{ value: number; label?: string }>;
  valueLabelFormat?: (value: number) => React.ReactNode;
  getAriaValueText?: (value: number) => string;
  valueLabelDisplay?: 'auto' | 'on' | 'off';
  disabled?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
}

export const MarkedSlider = forwardRef<HTMLSpanElement, MarkedSliderProperties>(
  (
    {
      min = 1,
      max = 5,
      step = 1,
      defaultValue,
      value,
      onChange,
      marks = [],
      valueLabelFormat,
      getAriaValueText,
      valueLabelDisplay = 'auto',
      disabled = false,
      sx,
      className,
      ...properties
    },
    reference
  ) => {
    return (
      <Box sx={{ width: 150, ...sx }} className={className}>
        <StyledSlider
          ref={reference}
          valueLabelDisplay={valueLabelDisplay}
          marks={marks}
          slots={{ thumb: ThumbComponent }}
          valueLabelFormat={valueLabelFormat}
          getAriaValueText={getAriaValueText}
          min={min}
          max={max}
          step={step}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          disabled={disabled}
          {...properties}
        />
      </Box>
    );
  }
);

MarkedSlider.displayName = 'MarkedSlider';
